// JURÍDICO — registrar o DISTRATO de um contrato (2026-08-26).
//
// Contrato que acaba antes da hora acaba por um papel. Aqui esse papel entra
// no sistema pelas mesmas duas portas do contrato:
//   • o PDF assinado do distrato (o caso comum) — a IA lê e preenche;
//   • redigir aqui, com numeração DIS-AAAA-NNN e assinatura digital.
//
// 🚨 O que decide tudo é a DATA DE EFEITO: é a partir dela que o contrato
//    deixa de valer, sai da aba Vigências e para de ser cobrado no e-mail de
//    segunda. Distrato assinado hoje com efeito daqui a 30 dias é comum — e
//    até lá o contrato continua valendo, de propósito.
//
// Modal próprio, e não mais um ramo do ModalIncluirAssinado: distrato pergunta
// outras coisas (data de efeito, motivo, quitação) e não pergunta nenhuma das
// do contrato (vigência, renovação, reajuste, foro). Ramificar aquele modal em
// dez lugares deixaria os dois piores.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, App as AntApp, Button, DatePicker, Flex, Form, Input, InputNumber,
  Modal, Select, Space, Spin, Tag, Typography, Upload,
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { subirArquivo } from '../administrativo-documentos/api';
import {
  lerContrato, statusLeitura, acervarContrato, distratarContrato,
  listarTiposContrato, ContratoLinha, TIPO_DISTRATO,
} from './api';

const { Text, Paragraph } = Typography;

type Etapa = 'escolher' | 'lendo' | 'conferir' | 'redigir';

export function ModalDistrato({ contrato, onFechar, onPronto }: {
  contrato: ContratoLinha | null;
  onFechar: () => void;
  onPronto: (r: { expediente_id?: string }) => void;
}) {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const [etapa, setEtapa] = useState<Etapa>('escolher');
  const [leituraId, setLeituraId] = useState<number | null>(null);
  const [campos, setCampos] = useState<Record<string, any>>({});
  const [sumario, setSumario] = useState<string | null>(null);
  const [tipos, setTipos] = useState<{ id: number; nome: string; prefixo: string }[]>([]);
  const [salvando, setSalvando] = useState(false);
  const inicioRef = useRef(0);

  const zerar = useCallback(() => {
    setEtapa('escolher'); setLeituraId(null); setCampos({}); setSumario(null);
    form.resetFields();
  }, [form]);

  useEffect(() => { if (contrato) zerar(); }, [contrato, zerar]);

  // ── Sobe o PDF e manda a IA ler (com o prompt de distrato) ──────────────
  async function subir(arquivo: File) {
    if (!contrato) return;
    setEtapa('lendo');
    inicioRef.current = Date.now();
    try {
      const s3_key = await subirArquivo(arquivo, 'contratos');
      const r = await lerContrato({ s3_key, arquivo_nome: arquivo.name, finalidade: 'distrato' });
      setLeituraId(r.leitura_id);
    } catch (e: any) {
      message.error('Falha ao enviar: ' + (e.response?.data?.error || e.message));
      setEtapa('escolher');
    }
  }

  // ── "Já leu?" ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (etapa !== 'lendo' || !leituraId) return;
    const id = setInterval(async () => {
      if (Date.now() - inicioRef.current > 4 * 60 * 1000) {
        clearInterval(id);
        message.warning('A leitura demorou demais. Preencha a data à mão.');
        setEtapa('conferir');
        return;
      }
      try {
        const { leitura } = await statusLeitura(leituraId);
        if (leitura.status === 'pronto') {
          clearInterval(id);
          setCampos(leitura.campos || {});
          setSumario(leitura.sumario_md);
          setEtapa('conferir');
        } else if (leitura.status === 'erro') {
          clearInterval(id);
          message.warning(leitura.erro || 'Não consegui ler esse arquivo — preencha à mão.');
          setEtapa('conferir');
        }
      } catch { /* rede oscilou; tenta no próximo tique */ }
    }, 3000);
    return () => clearInterval(id);
  }, [etapa, leituraId, message]);

  // ── Preenche o formulário com o que a IA leu ───────────────────────────
  useEffect(() => {
    if (etapa !== 'conferir') return;
    const pontos = (campos.pontos_de_atencao || []) as string[];
    form.setFieldsValue({
      data_efeito: campos.data_efeito ? dayjs(campos.data_efeito) : null,
      valor: campos.valor ?? null,
      observacoes: [
        campos.motivo ? `Motivo: ${campos.motivo}` : '',
        campos.ha_quitacao === true ? 'O documento dá quitação mútua às partes.' : '',
        campos.ha_quitacao === false ? 'ATENÇÃO: o documento NÃO menciona quitação.' : '',
        pontos.length ? `Pontos de atenção:\n- ${pontos.join('\n- ')}` : '',
      ].filter(Boolean).join('\n'),
    });
  }, [etapa, campos, form]);

  async function abrirRedigir() {
    try {
      const r = await listarTiposContrato();
      // O tipo do distrato é o de código 'distrato' — pelo CÓDIGO, não pela
      // sigla: desde 15/09 as siglas se editam na tela de tipos de contrato.
      const so = (r.tipos || []).filter((t: any) => t.codigo === TIPO_DISTRATO);
      // Sem o tipo Distrato, NÃO oferece a lista inteira: um distrato redigido
      // como "Contrato de mútuo" numeraria MUT-… e o servidor o recusaria.
      if (!so.length) {
        message.error('O tipo Distrato não está disponível. Avise quem administra os tipos de contrato.');
        return;
      }
      setTipos(so);
      form.resetFields();
      setEtapa('redigir');
    } catch (e: any) {
      message.error('Falha ao carregar os tipos: ' + (e.response?.data?.error || e.message));
    }
  }

  async function registrar() {
    if (!contrato) return;
    let v: any;
    try { v = await form.validateFields(); } catch { return; }
    const data_efeito = dayjs(v.data_efeito).format('YYYY-MM-DD');
    setSalvando(true);
    try {
      if (etapa === 'redigir') {
        const r = await distratarContrato({
          contrato_id: contrato.id, data_efeito, tipo_id: v.tipo_id,
          observacoes: v.observacoes || null,
        });
        message.success('Distrato criado. Abrindo o editor para você escrever o texto.');
        onPronto({ expediente_id: r.expediente_id });
        return;
      }
      // Com PDF: o arquivo vai para o acervo e a ficha do distrato nasce
      // ligada ao contrato. Quem grava é o mesmo caminho do contrato assinado.
      await acervarContrato({
        leitura_id: leituraId,
        distrato_de: contrato.id,
        data_efeito,
        titulo: `Distrato — ${contrato.contraparte_nome || contrato.codigo || ''}`.trim(),
        contraparte_nome: contrato.contraparte_nome,
        valor: v.valor ?? null,
        observacoes: v.observacoes || null,
      });
      message.success('Distrato registrado. O contrato passa a constar rescindido '
        + `a partir de ${dayjs(data_efeito).format('DD/MM/YYYY')}.`);
      onPronto({});
    } catch (e: any) {
      const st = e?.response?.status;
      message.error(st === 409
        ? (e.response?.data?.error || 'Esse contrato já tem um distrato.')
        : 'Falha ao registrar: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  const rotulo = contrato
    ? `${contrato.codigo || 'contrato'} · ${contrato.contraparte_nome || ''}`.trim() : '';

  return (
    <Modal
      open={!!contrato} onCancel={onFechar} footer={null}
      title={`Registrar distrato — ${rotulo}`}
      width="94vw" style={{ maxWidth: 760, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 130px)', overflowY: 'auto' } }}
      destroyOnHidden
    >
      {etapa === 'escolher' && (
        <>
          <Paragraph type="secondary" style={{ fontSize: 12 }}>
            O distrato é o papel que encerra este contrato antes do prazo. Ele entra no acervo
            como instrumento próprio, ligado ao contrato — e a partir da data de efeito o
            contrato passa a constar rescindido, sai da aba Vigências e não é mais cobrado no
            e-mail de segunda.
          </Paragraph>
          <Upload.Dragger
            accept=".pdf,.docx" showUploadList={false} maxCount={1}
            beforeUpload={(f) => { subir(f as File); return false; }}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Solte aqui o distrato assinado</p>
            <p className="ant-upload-hint">
              PDF ou Word. A IA lê e traz a data de efeito, o motivo e se há quitação —
              você confere antes de salvar.
            </p>
          </Upload.Dragger>
          <Flex justify="center" style={{ marginTop: 12 }}>
            <Button type="link" onClick={abrirRedigir}>
              Ainda não temos o distrato assinado — quero redigir um aqui
            </Button>
          </Flex>
        </>
      )}

      {etapa === 'lendo' && (
        <Flex vertical align="center" gap={10} style={{ padding: '48px 0' }}>
          <Spin />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Lendo o distrato… leva de 20 a 60 segundos.
          </Text>
        </Flex>
      )}

      {(etapa === 'conferir' || etapa === 'redigir') && (
        <>
          <Alert
            type="warning" showIcon style={{ marginBottom: 12 }}
            message={etapa === 'redigir'
              ? 'O texto você escreve no editor, depois de criar'
              : 'Confira antes de registrar'}
            description={
              <span style={{ fontSize: 12 }}>
                A <b>data de efeito</b> é o que encerra o contrato: até ela chegar, ele
                continua valendo e sendo cobrado. Ela pode ser diferente da data de
                assinatura do distrato.
              </span>
            }
          />
          <Form form={form} layout="vertical" size="small">
            {etapa === 'redigir' && (
              <Form.Item name="tipo_id" label="Tipo" rules={[{ required: true }]}
                initialValue={tipos[0]?.id}>
                <Select options={tipos.map((t) => ({ value: t.id, label: `${t.prefixo} — ${t.nome}` }))} />
              </Form.Item>
            )}
            <Flex gap={12} wrap>
              <Form.Item name="data_efeito" style={{ flex: '0 0 200px' }}
                label={
                  <Space size={4}>
                    Contrato encerra em
                    {campos.data_efeito && (
                      <Tag color="gold" style={{ fontSize: 10, margin: 0 }}>do PDF</Tag>
                    )}
                  </Space>
                }
                rules={[{ required: true, message: 'Informe a data de efeito' }]}>
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
              {etapa === 'conferir' && (
                <Form.Item name="valor" label="Valor envolvido (R$)" style={{ flex: '0 0 190px' }}
                  tooltip="Saldo a pagar ou a devolver por causa da rescisão, se houver.">
                  <InputNumber style={{ width: '100%' }} min={0} decimalSeparator="," />
                </Form.Item>
              )}
            </Flex>
            <Form.Item name="observacoes" label="Motivo e observações">
              <Input.TextArea rows={4} />
            </Form.Item>
          </Form>

          {sumario && (
            <details style={{ marginBottom: 12 }}>
              <summary style={{ cursor: 'pointer', fontSize: 12, color: '#666' }}>
                O que a IA entendeu do distrato
              </summary>
              <Paragraph style={{ fontSize: 12, whiteSpace: 'pre-wrap', marginTop: 8 }}>
                {sumario}
              </Paragraph>
            </details>
          )}

          <Flex justify="space-between" wrap gap={8}>
            <Button onClick={zerar}>Voltar</Button>
            <Space>
              <Button onClick={onFechar}>Cancelar</Button>
              <Button type="primary" loading={salvando} onClick={registrar}>
                {etapa === 'redigir' ? 'Criar e abrir o texto' : 'Registrar distrato'}
              </Button>
            </Space>
          </Flex>
        </>
      )}
    </Modal>
  );
}
